import React, { useState, useEffect } from 'react';
import PageWrapper from './PageWrapper';
import Page from './Page';
import { MenuBar } from '@go-client/components';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { BubbleButton } from '@go-client/components/BubbleUI';
import { getLocaleUTCDateTimeString, parseDate } from '@go-shared/lib/time';
import { useNotifications } from '@go-client/context/NotificationsContext';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import NotificationTableView, {
  NotificationTableItem,
} from '@go-client/components/NotificationTableView';
import { partition, notUndefined } from '@go-shared/lib/functional';
import { getNotifications, markAllAsRead } from '@go-client/lib/api/userNotification';
import { BareUserNotification, UserNotificationTier } from '@go-shared/types/userNotification';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { Property } from '@go-shared/types/property';
import { usePropertyInfo } from '@go-client/context/PropertyInfoContext';
import { safeIdCompare } from '@go-shared/lib/utils';

interface NotificationParams {
  property: string;
}

type NotificationPageProps = RouteComponentProps<NotificationParams>;

export default function Notifications(props: NotificationPageProps) {
  const [activeView, setActiveView] = useState<'new' | 'history'>('new');
  const [historyNotifs, setHistoryNotifs] = useState<BareUserNotification[]>([]);

  const { user } = useUserInfo();
  const { brand } = useServerInfo();
  const { allProperties: properties } = usePropertyInfo();
  const { notifications, refreshContext: fetchNotifications } = useNotifications();

  useEffect(() => {
    fetchHistory().catch(console.error);
  }, []);

  const tableItems: NotificationTableItem[] = notifications
    .map(convertNotifToTableItem)
    .filter(notUndefined);
  const historyTableItems: NotificationTableItem[] = historyNotifs
    .map(convertNotifToTableItem)
    .filter(notUndefined);

  const [highTableItems, lowTableItems] = partition(tableItems, ti => ti._data.tier > 1);

  function convertNotifToTableItem(notif: BareUserNotification): NotificationTableItem | undefined {
    const createdAtDate = parseDate(notif.createdAt);

    const createdAt = createdAtDate ? getLocaleUTCDateTimeString(createdAtDate) : notif.createdAt;
    let property: Property | undefined;

    if (notif.property) {
      // this is a property specific notification
      property = properties.find(p => safeIdCompare(p._id, notif.property));
      if (!property) return undefined;
    }

    return {
      id: notif._id,
      date: createdAt,
      title: notif.title,
      category: notif.category,
      description: notif.description,
      propertyName: property ? property.name : '',
      _data: {
        ...notif,
        property,
      },
    };
  }

  async function refreshNotifications() {
    await Promise.all([fetchNotifications(), fetchHistory()]);
  }

  async function fetchHistory() {
    const hNotifs = await getNotifications('read');
    setHistoryNotifs(hNotifs);
  }

  async function handleOnMarkAllAsRead(tier: UserNotificationTier) {
    await markAllAsRead(tier);
    await refreshNotifications();
  }

  return (
    <PageWrapper {...props}>
      <Page className="Notifications absolute flex items-stretch">
        <MenuBar {...props} />
        <div className="NotificationsContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="NotificationsHeader align-with-logo flex items-end sticky top-0
                left-0 z-10 bg-white shadow"
          >
            <BrandLogo logo={brand.logo} />
            <div className="flex-grow px-5 flex items-end">
              <div className="mb-3">
                <BubbleButton
                  appearance="triangular"
                  onClick={() => setActiveView('new')}
                  variant={activeView === 'new' ? 'navy' : 'smoke'}
                  className={`text-base-i w-48 pl-5-i text-left mx-3 ${
                    activeView === 'history' ? 'font-normal-i' : ''
                  }`}
                >
                  Alerts
                </BubbleButton>
                <BubbleButton
                  appearance="triangular"
                  onClick={() => setActiveView('history')}
                  variant={activeView === 'history' ? 'navy' : 'smoke'}
                  className={`text-base-i w-48 pl-5-i text-left whitespace-nowrap ${
                    activeView === 'new' ? 'font-normal-i' : ''
                  }`}
                >
                  Alert History
                </BubbleButton>
              </div>
            </div>
          </div>
          <div className="NotificationsBody mx-10 content-minus-logo min-w-900">
            <div className="py-8">
              {activeView === 'new' ? (
                <>
                  <div className="flex items-end mb-2">
                    <h2 className="text-xl text-navy mr-auto">Important</h2>
                    {highTableItems.length > 0 && (
                      <BubbleButton
                        variant="navy"
                        inverse
                        onClick={() => handleOnMarkAllAsRead(2)}
                        icon="check-double"
                      >
                        Mark All As Read
                      </BubbleButton>
                    )}
                  </div>

                  <NotificationTableView
                    className="mb-8"
                    keyExtractor={item => item.id}
                    stickyHeader={130}
                    responsive
                    headVariant="navy"
                    user={user}
                    tableItems={highTableItems}
                    refreshNotifications={refreshNotifications}
                  />
                  <div className="flex items-end mb-2">
                    <h2 className="text-xl text-navy mr-auto">Miscellaneous</h2>
                    {lowTableItems.length > 0 && (
                      <BubbleButton
                        variant="navy"
                        inverse
                        onClick={() => handleOnMarkAllAsRead(1)}
                        icon="check-double"
                      >
                        Mark All As Read
                      </BubbleButton>
                    )}
                  </div>
                  <NotificationTableView
                    keyExtractor={item => item.id}
                    stickyHeader={130}
                    responsive
                    headVariant="slate"
                    user={user}
                    tableItems={lowTableItems}
                    refreshNotifications={refreshNotifications}
                  />
                </>
              ) : (
                <NotificationTableView
                  keyExtractor={item => item.id}
                  stickyHeader={130}
                  responsive
                  headVariant="slate"
                  user={user}
                  tableItems={historyTableItems}
                  refreshNotifications={refreshNotifications}
                />
              )}
            </div>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
}
